<script setup>
import { computed, onBeforeUnmount, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';

const props = defineProps({
  websiteToken: {
    type: String,
    default: '',
  },
  websiteUrl: {
    type: String,
    default: '',
  },
  launcherTitle: {
    type: String,
    default: '',
  },
});

const { t } = useI18n();
const previewUrl = ref('');
const backdropBlocked = ref(false);
const backdropConfirmed = ref(false);
const backdropLoaded = ref(false);

const normalizedBackdropUrl = computed(() => {
  const raw = (props.websiteUrl || '').trim();
  if (!raw) return '';
  try {
    const withScheme = /^https?:\/\//i.test(raw) ? raw : `https://${raw}`;
    const url = new URL(withScheme);
    if (!url.host) return '';
    return url.toString();
  } catch {
    return '';
  }
});

const backdropHost = computed(() => {
  if (!normalizedBackdropUrl.value) return '';
  try {
    return new URL(normalizedBackdropUrl.value).host;
  } catch {
    return '';
  }
});

const showSiteBackdrop = computed(
  () => Boolean(normalizedBackdropUrl.value) && !backdropBlocked.value
);

function revokePreviewUrl() {
  if (previewUrl.value) {
    URL.revokeObjectURL(previewUrl.value);
    previewUrl.value = '';
  }
}

function buildPreviewHtml(token) {
  const origin = window.location.origin;
  const title =
    props.launcherTitle?.trim() ||
    t('ONBOARDING_WIZARD.FINISH.WIDGET_LAUNCHER_TITLE');

  return `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="utf-8" />
  <meta name="viewport" content="width=device-width, initial-scale=1" />
  <style>
    html, body {
      margin: 0;
      width: 100%;
      height: 100%;
      background: transparent !important;
      overflow: hidden;
    }
  </style>
</head>
<body>
  <script>
    window.rookooSettings = {
      type: 'bar',
      bubbleIconName: 'calendar',
      launcherTitle: ${JSON.stringify(title)},
      position: 'right'
    };
    (function (d, t) {
      var BASE_URL = ${JSON.stringify(origin)};
      var g = d.createElement(t);
      var s = d.getElementsByTagName(t)[0];
      g.src = BASE_URL + '/packs/js/v2sdk.js';
      g.defer = true;
      g.async = true;
      s.parentNode.insertBefore(g, s);
      g.onload = function () {
        if (!window.rookooSDK || !window.rookooSDK.run) return;
        window.rookooSDK.run({
          websiteToken: ${JSON.stringify(token)},
          baseUrl: BASE_URL
        });
        window.addEventListener('rookoo:ready', function onReady() {
          window.removeEventListener('rookoo:ready', onReady);
          window.setTimeout(function () {
            if (window.$rookoo && typeof window.$rookoo.toggle === 'function') {
              window.$rookoo.toggle('open');
            }
          }, 700);
        });
      };
    })(document, 'script');
  <\/script>
</body>
</html>`;
}

function refreshPreview() {
  revokePreviewUrl();
  backdropBlocked.value = false;
  backdropConfirmed.value = false;
  backdropLoaded.value = false;
  if (!props.websiteToken) return;
  const blob = new Blob([buildPreviewHtml(props.websiteToken)], {
    type: 'text/html',
  });
  previewUrl.value = URL.createObjectURL(blob);
}

function onBackdropLoad(event) {
  backdropLoaded.value = true;
  // Once a real cross-origin page has painted, keep it — later blank
  // load events (about:blank) must not hide the site again.
  if (backdropConfirmed.value) return;

  try {
    // contentDocument is null for cross-origin (no throw) and was wrongly
    // treated as "blocked". contentWindow.document throws when framed.
    const doc = event.target.contentWindow.document;
    if (!doc?.body || doc.body.childElementCount === 0) {
      backdropBlocked.value = true;
      return;
    }
    backdropConfirmed.value = true;
    backdropBlocked.value = false;
  } catch {
    backdropConfirmed.value = true;
    backdropBlocked.value = false;
  }
}

function onBackdropError() {
  if (backdropConfirmed.value) return;
  backdropBlocked.value = true;
}

const hasPreview = computed(() => Boolean(previewUrl.value));

watch(
  () => [props.websiteToken, props.launcherTitle, props.websiteUrl],
  () => refreshPreview(),
  { immediate: true }
);

onBeforeUnmount(() => {
  revokePreviewUrl();
});
</script>

<template>
  <div
    class="relative w-full h-[min(72vh,760px)] rounded-2xl border border-slate-100 dark:border-slate-800 overflow-hidden bg-slate-50 dark:bg-slate-900"
  >
    <!-- Fallback surface when the venue site blocks iframes -->
    <div
      class="absolute inset-0"
      :class="
        showSiteBackdrop
          ? 'opacity-0'
          : 'opacity-100 bg-[radial-gradient(circle_at_top_right,rgba(118,99,171,0.12),transparent_40%),linear-gradient(180deg,#f8fafc_0%,#eef2ff_100%)] dark:bg-slate-900'
      "
    >
      <p
        v-if="backdropHost"
        class="m-0 p-7 text-sm text-slate-500 dark:text-slate-400"
      >
        <span class="font-semibold text-slate-900 dark:text-white">{{
          backdropHost
        }}</span>
        ·
        {{ t('ONBOARDING_WIZARD.FINISH.WIDGET_PREVIEW_FALLBACK') }}
      </p>
    </div>

    <!-- Venue site backdrop (when framing is allowed) -->
    <iframe
      v-if="normalizedBackdropUrl"
      :src="normalizedBackdropUrl"
      title=""
      class="absolute inset-0 w-full h-full border-0 pointer-events-none"
      :class="showSiteBackdrop ? 'opacity-100' : 'opacity-0'"
      loading="lazy"
      referrerpolicy="no-referrer-when-downgrade"
      @load="onBackdropLoad"
      @error="onBackdropError"
    />

    <!-- Transparent v2 widget layer -->
    <iframe
      v-if="hasPreview"
      :src="previewUrl"
      :title="t('ONBOARDING_WIZARD.FINISH.WIDGET_PREVIEW_TITLE')"
      class="absolute inset-0 w-full h-full border-0 bg-transparent"
      allow="microphone; camera; geolocation"
      allowtransparency="true"
    />
    <p
      v-else
      class="absolute inset-0 flex items-center justify-center text-sm text-slate-500 dark:text-slate-400 m-0 p-6"
    >
      {{ t('ONBOARDING_WIZARD.FINISH.WIDGET_MISSING') }}
    </p>

    <div
      v-if="backdropHost && showSiteBackdrop && backdropLoaded"
      class="absolute top-3 left-3 z-10 rounded-full bg-white/90 dark:bg-slate-900/90 px-3 py-1 text-xs font-medium text-slate-600 dark:text-slate-300 shadow-sm pointer-events-none"
    >
      {{ backdropHost }}
    </div>
  </div>
</template>
